import React, { useEffect, useRef, useState } from 'react';
import generateTestId from '~/helpers/testIdHelpers';
import editorAutocorrect from 'packages/pages/editor/EditorAutocorrectRules';
import { useInputBlur } from 'packages/hooks/useInputBlur';
import { StyledInput } from '../index.styled';

type Props = {
    id: string;
    value: number;
    onChange: (value: number) => void;
    testId: string;
    isMixed?: boolean;
    isDisabled?: boolean;
};

export function OpacityInput({ value, isMixed, isDisabled, onChange, testId, id }: Props) {
    const [displayValue, setDisplayValue] = useState(toInputValue(value, isMixed));
    const [isFocused, setIsFocused] = useState(false);
    const { onFocus, unsubscribeListener } = useInputBlur();
    const ref = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (isFocused) {
            return;
        }

        setDisplayValue(toInputValue(value, isMixed));
    }, [value, isMixed]);

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            // @ts-ignore
            event.target.blur();

            return;
        }

        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();

            const sanitized = sanitizeInputValue(displayValue);

            setDisplayValue(sanitized);

            const parsed = parseInputValue(sanitized);

            if (isNaN(parsed)) {
                return;
            }

            const numberToAdd = event.key === 'ArrowDown' ? -1 : 1;
            const factor = event.shiftKey ? 10 : 1;

            const val = parsed + numberToAdd * factor;
            const correctValue = editorAutocorrect('text_element_opacity', val);

            onChange(correctValue);
            setDisplayValue(correctValue.toString());
        }
    };

    const handleFocus = () => {
        onFocus();
        setIsFocused(true);

        if (isMixed) {
            setDisplayValue('');
        }

        setDisplayValue((prev) => prev.replace('%', ''));
    };

    const onBlur = () => {
        unsubscribeListener();
        setIsFocused(false);

        if (displayValue === '') {
            return;
        }

        const parsed = parseInputValue(displayValue);
        const normalized = isNaN(parsed) ? 0 : parsed;

        setDisplayValue(toInputValue(clamp(normalized, 0, 100), isMixed));
        onChange(clamp(normalized, 0, 100));
    };

    const handleChange = (newValue) => {
        // Remove all non-numeric characters
        const sanitized = sanitizeInputValue(newValue);

        setDisplayValue(sanitized);
    };

    return (
        <StyledInput
            id={id}
            type="text"
            inputRef={ref}
            value={displayValue}
            placeholder={isMixed && !isFocused ? 'Mixed' : ''}
            onFocus={handleFocus}
            onBlur={onBlur}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            isDisabled={isDisabled}
            {...generateTestId(testId)}
        />
    );
}

function parseInputValue(value: string) {
    return parseInt(value || '0', 10);
}

function toInputValue(value: number, isMixed) {
    if (isMixed) {
        return '';
    }

    return value.toString() + '%';
}

function sanitizeInputValue(value: string) {
    // remove leading zeros and non-numeric characters
    return value.replace(/^0+(?=\d)/, '');
}

function clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max);
}
